import { useState, useEffect, useMemo } from "react";
import { safeHref } from "../lib/safeUrl";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import TaxonomyManager from "../components/TaxonomyManager";
import AdminManageMenu from "../components/AdminManageMenu";
import { useTaxonomy } from "../hooks/useTaxonomy";
import { 
  Plus, Calendar, MapPin, ExternalLink, Users, Globe, Video, Ship, Clock, ShieldCheck
} from "lucide-react";

// Icons are a code-only concern, indexed by taxonomy key. Any new event
// type added via the admin Manage-list gets the generic Calendar icon by
// default — UI still works, founder can request a custom icon later.
const EVENT_TYPE_ICONS = {
  conference: Users,
  ce_course: Users,
  seminar: Users,
  webinar: Video,
  workshop: Users,
  cruise: Ship,
  study_session: Users,
  regulatory: ShieldCheck,
  social: Users,
};

// Fallback list used only when the live taxonomy fetch is empty. Canonical
// source is the founder-managed `event_categories` taxonomy.
const FALLBACK_EVENT_TYPES = [
  { value: "conference",  label: "Conference" },
  { value: "ce_course",   label: "CE course" },
  { value: "workshop",    label: "Workshop" },
  { value: "seminar",     label: "Seminar / webinar" },
  { value: "study_session","label": "Study session" },
  { value: "regulatory",  label: "Regulatory / college event" },
  { value: "social",      label: "Social / networking" },
];

const EventsPage = () => {
  const { user } = useAuth();
  const { items: eventTypeItems, refetch: refetchEventTypes } = useTaxonomy("event_categories");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventType, setEventType] = useState("");
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    event_type: "conference",
    location: "",
    province: "",
    url: "",
    start_date: "",
    end_date: "",
    is_virtual: false,
    organizer: "",
    registration_url: ""
  });

  // Live-bound list of event-type options. Adds/renames/soft-deletes from
  // the admin Manage-list flow propagate to the submit form + filter chips
  // on the next render with no page refresh.
  const EVENT_TYPES = useMemo(() => {
    if (eventTypeItems && eventTypeItems.length > 0) {
      return eventTypeItems.map(it => ({
        value: it.key,
        label: it.label,
        icon: EVENT_TYPE_ICONS[it.key] || Calendar,
      }));
    }
    return FALLBACK_EVENT_TYPES.map(t => ({ ...t, icon: EVENT_TYPE_ICONS[t.value] || Calendar }));
  }, [eventTypeItems]);

  const PROVINCES = [
    { value: "AB", label: "Alberta" },
    { value: "BC", label: "British Columbia" },
    { value: "MB", label: "Manitoba" },
    { value: "NB", label: "New Brunswick" },
    { value: "NL", label: "Newfoundland and Labrador" },
    { value: "NS", label: "Nova Scotia" },
    { value: "NT", label: "Northwest Territories" },
    { value: "NU", label: "Nunavut" },
    { value: "ON", label: "Ontario" },
    { value: "PE", label: "Prince Edward Island" },
    { value: "QC", label: "Quebec" },
    { value: "SK", label: "Saskatchewan" },
    { value: "YT", label: "Yukon" },
  ];

  const EVENT_TYPE_LABELS = useMemo(() => {
    const map = {};
    EVENT_TYPES.forEach(t => { map[t.value] = t.label; });
    return map;
  }, [EVENT_TYPES]);

  const [provinceFilter, setProvinceFilter] = useState("");

  // Pre-compute the filtered + sorted event list so we don't redo the work
  // on every render (e.g. when toggling unrelated state like dropdowns).
  const visibleEvents = useMemo(() => {
    return events
      .filter(ev => !provinceFilter || ev.province === provinceFilter)
      .slice()
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  }, [events, provinceFilter]);

  // If the founder deletes/renames the current event_type key, fall back
  // to the first available type so the form never tries to submit a stale
  // value that no longer exists in the taxonomy.
  useEffect(() => {
    if (EVENT_TYPES.length > 0 && !EVENT_TYPES.find(t => t.value === newEvent.event_type)) {
      setNewEvent(e => ({ ...e, event_type: EVENT_TYPES[0].value }));
    }
  }, [EVENT_TYPES, newEvent.event_type]);

  useEffect(() => {
    fetchEvents();
  }, [eventType]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (eventType && eventType !== "all") params.append("event_type", eventType);

      const response = await axios.get(`${API}/events?${params.toString()}`);
      setEvents(response.data);
    } catch (error) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title.trim() || !newEvent.start_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/events`, {
        ...newEvent,
        start_date: new Date(newEvent.start_date).toISOString(),
        end_date: newEvent.end_date ? new Date(newEvent.end_date).toISOString() : null
      });
      toast.success("Event submitted! It will be visible after AI verification.");
      setShowNewEvent(false);
      setNewEvent({
        title: "",
        description: "",
        event_type: "conference",
        location: "",
        url: "",
        start_date: "",
        end_date: "",
        is_virtual: false,
        organizer: "",
        registration_url: ""
      });
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create event");
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-CA", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getEventTypeIcon = (type) => {
    const found = EVENT_TYPES.find(t => t.value === type);
    return found ? found.icon : Calendar;
  };

  const isVerified = user?.verification_status === "verified";

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="events-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] inline-flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <EditableText blockKey="events.title" fallback="Professional Events" as="span" />
              <TaxonomyManager
                kind="event_categories"
                title="Event categories"
                description="Manage event type tags."
                triggerLabel="Manage event categories"
                onChange={refetchEventTypes}
              />
            </h1>
            <EditableText
              blockKey="events.subtitle"
              fallback="Verified dental conferences, CE events, and seminars across Canada"
              as="p"
              className="text-[#64748B] mt-1"
              multiline
            />
          </div>
          {isVerified && (
            <Dialog open={showNewEvent} onOpenChange={setShowNewEvent}>
              <DialogTrigger asChild>
                <Button className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" data-testid="add-event-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Event
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Add an Event
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateEvent} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="title">Event Title *</Label>
                    <Input
                      id="title"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                      placeholder="Conference name"
                      className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                      data-testid="event-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="event_type">Event Type</Label>
                    <Select
                      value={newEvent.event_type}
                      onValueChange={(value) => setNewEvent({ ...newEvent, event_type: value })}
                    >
                      <SelectTrigger className="mt-2 bg-white border-[#E5DFEC]" data-testid="event-type-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EVENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value} data-testid={`event-type-${type.value}`}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                      placeholder="What is this event about?"
                      className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                      data-testid="event-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_date">Start Date *</Label>
                      <Input
                        id="start_date"
                        type="datetime-local"
                        value={newEvent.start_date}
                        onChange={(e) => setNewEvent({ ...newEvent, start_date: e.target.value })}
                        className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                        data-testid="event-start-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">End Date</Label>
                      <Input
                        id="end_date"
                        type="datetime-local"
                        value={newEvent.end_date}
                        onChange={(e) => setNewEvent({ ...newEvent, end_date: e.target.value })}
                        className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_virtual"
                      checked={newEvent.is_virtual}
                      onCheckedChange={(checked) => setNewEvent({ ...newEvent, is_virtual: checked })}
                    />
                    <Label htmlFor="is_virtual" className="cursor-pointer">This is a virtual event</Label>
                  </div>
                  {!newEvent.is_virtual && (
                    <>
                      <div>
                        <Label htmlFor="province">Province *</Label>
                        <Select
                          value={newEvent.province}
                          onValueChange={(v) => setNewEvent({ ...newEvent, province: v })}
                        >
                          <SelectTrigger id="province" className="mt-2 bg-white border-[#E5DFEC]" data-testid="event-province-input">
                            <SelectValue placeholder="Select province" />
                          </SelectTrigger>
                          <SelectContent>
                            {PROVINCES.map(p => (
                              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="location">City / Venue</Label>
                        <Input
                          id="location"
                          value={newEvent.location}
                          onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                          placeholder="City or venue name"
                          className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                        />
                      </div>
                    </>
                  )}
                  <div>
                    <Label htmlFor="organizer">Organizer</Label>
                    <Input
                      id="organizer"
                      value={newEvent.organizer}
                      onChange={(e) => setNewEvent({ ...newEvent, organizer: e.target.value })}
                      placeholder="Who is organizing this event?"
                      className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="registration_url">Registration URL</Label>
                    <Input
                      id="registration_url"
                      type="url"
                      value={newEvent.registration_url}
                      onChange={(e) => setNewEvent({ ...newEvent, registration_url: e.target.value })}
                      placeholder="https://..."
                      className="mt-2 bg-white border-[#E5DFEC] focus:border-[#6B4E8A]"
                    />
                  </div>
                  <div className="bg-[#FAF7F2] rounded-xl p-4 text-sm text-[#64748B]">
                    <p>Events are verified by AI to ensure they are legitimate dental/healthcare related events. Your event will be visible immediately if verified.</p>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowNewEvent(false)} className="rounded-full">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-full" disabled={creating} data-testid="submit-event-btn">
                      {creating ? "Submitting..." : "Submit Event"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E5DFEC] rounded-2xl p-4 mb-6 space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={eventType === "" ? "default" : "outline"}
              onClick={() => setEventType("")}
              className={`rounded-full ${eventType === "" ? "bg-[#6B4E8A] text-white" : ""}`}
            >
              All Events
            </Button>
            {EVENT_TYPES.map((type) => (
              <Button
                key={type.value}
                variant={eventType === type.value ? "default" : "outline"}
                onClick={() => setEventType(type.value)}
                className={`rounded-full ${eventType === type.value ? "bg-[#6B4E8A] text-white" : ""}`}
              >
                {type.label}
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Label className="text-xs text-[#64748B]">Province:</Label>
            <Select value={provinceFilter || "all"} onValueChange={(v) => setProvinceFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 bg-white border-[#E5DFEC] h-9 text-sm" data-testid="event-province-filter">
                <SelectValue placeholder="All provinces" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All provinces</SelectItem>
                {PROVINCES.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {provinceFilter && (
              <button
                type="button"
                onClick={() => setProvinceFilter("")}
                className="text-xs text-[#6B4E8A] hover:underline"
              >Clear</button>
            )}
          </div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border border-[#E5DFEC] rounded-2xl p-6 animate-pulse">
                <div className="h-12 w-12 bg-[#F0ECF7] rounded-xl mb-4"></div>
                <div className="h-6 bg-[#F0ECF7] rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-[#F0ECF7] rounded w-full mb-2"></div>
                <div className="h-4 bg-[#F0ECF7] rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleEvents.map((event, idx) => {
              const TypeIcon = getEventTypeIcon(event.event_type);
              return (
                <motion.div
                  key={event.event_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="relative bg-white border border-[#E5DFEC] rounded-2xl p-6 card-hover"
                >
                  <div className="absolute top-3 right-3 z-10">
                    <AdminManageMenu
                      resourceType="event"
                      resourceId={event.event_id}
                      resourceLabel={event.title}
                      onDeleted={fetchEvents}
                    />
                  </div>
                  <div className="flex items-start justify-between mb-4 pr-8">
                    <div className="w-12 h-12 rounded-xl bg-[#F0ECF7] flex items-center justify-center">
                      <TypeIcon className="w-6 h-6 text-[#8DB5A0]" />
                    </div>
                    {event.is_virtual && (
                      <span className="px-3 py-1 bg-[#8DB5A0]/20 text-[#8DB5A0] text-xs font-medium rounded-full flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        Virtual
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-medium text-[#6B4E8A] mb-2 line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {event.title}
                  </h3>
                  <p className="text-sm text-[#64748B] line-clamp-2 mb-4">
                    {event.description}
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-[#64748B]">
                      <Calendar className="w-4 h-4 text-[#8DB5A0]" />
                      {formatDate(event.start_date)}
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-2 text-sm text-[#64748B]">
                        <MapPin className="w-4 h-4 text-[#8DB5A0]" />
                        {event.location}
                      </div>
                    )}
                    {event.organizer && (
                      <div className="flex items-center gap-2 text-sm text-[#64748B]">
                        <Users className="w-4 h-4 text-[#8DB5A0]" />
                        {event.organizer}
                      </div>
                    )}
                  </div>
                  {safeHref(event.registration_url) && (
                    <a
                      href={safeHref(event.registration_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-[#8DB5A0] hover:text-[#D0694E]"
                    >
                      Register <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {(event.ai_verified && (event.evidence_url || event.url)) && (() => {
                    try {
                      const host = new URL(event.evidence_url || event.url).hostname.replace(/^www\./, "");
                      return (
                        <div className="mt-3 pt-3 border-t border-[#E5DFEC] flex items-center gap-1.5 text-[11px] text-[#8DB5A0]" data-testid={`event-verified-${event.event_id}`}>
                          <ShieldCheck className="w-3.5 h-3.5" />
                          <span>Verified via {host}</span>
                        </div>
                      );
                    } catch {
                      return null;
                    }
                  })()}
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16 bg-white border border-[#E5DFEC] rounded-2xl">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-[#E5DFEC]" />
            <h3 className="text-lg font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              No upcoming events
            </h3>
            <p className="text-[#64748B] mb-4">
              {eventType ? "No events found for this category" : "Be the first to add an event!"}
            </p>
            {isVerified && (
              <Button onClick={() => setShowNewEvent(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            )}
          </div>
        )}

        <div className="mt-8 bg-[#F0ECF7]/70 border border-[#E5DFEC] rounded-xl p-3 flex items-start gap-2" data-testid="events-disclaimer">
          <Calendar className="w-4 h-4 text-[#6B4E8A] mt-0.5 shrink-0" />
          <p className="text-xs text-[#6B4E8A] leading-relaxed">
            DHDH makes reasonable efforts to verify listed events but is not responsible for changes, cancellations, or inaccuracies in event details. Always confirm directly with the event organizer.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default EventsPage;
